import { NgModule, InjectionToken } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { StoreModule, ActionReducerMap } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ReactiveFormsModule } from '@angular/forms';

import { CarouselModule } from '@app/modules/carousel/carousel.module';
import { ImageModule } from '@ux/components/image/image.module';
import { GlobalPipesModule } from '@app/pipes/global-pipes.module';
import { FlightsWidgetComponent } from '@shared/widgets/components/flights-widget/flights-widget.component';
import { GeneralWidgetComponent } from '@shared/widgets/components/general-widget/general-widget.component';
import { WidgetsContainerComponent } from '@shared/widgets/components/widgets-container/widgets-container.component';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import { RefundableWidgetComponent } from '@shared/widgets/components/refundable-widget/refundable-widget.component';
import { AlertsWidgetComponent } from '@shared/widgets/components/alerts-widget/alerts-widget.component';
import { widgetsFeatureName, WidgetsState } from '@shared/widgets/store/state/widgets.state';
import { widgetsRootReducer } from '@shared/widgets/store/reducers';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { AlertsEffects } from '@shared/widgets/store/effects/alerts.effects';
import { AlertsService } from '@shared/widgets/services/alerts.service';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { AlertNotificationComponent } from '@shared/widgets/components/alert-notification/alert-notification.component';
import { ModalModule } from '@ux/components/modal/modal.module';
import { SwipeUpModule } from '@ux/components/swipe-up/swipe-up.module';
import { TopZonesWidgetComponent } from '@shared/widgets/components/top-zones-widget/top-zones-widget.component';
import { CountryNamePipe } from '@app/pipes/country-name.pipe';
import { BookingFacade } from '@booking/booking.facade';
import { AlertFormComponent } from '@shared/widgets/components/alert-form/alert-form.component';
import { FormCurrencyInputModule } from '@forms/form-currency-input/form-currency-input.module';
import { DeleteAlertFormComponent } from '@shared/widgets/components/delete-alert-form/delete-alert-form.component';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { MapWidgetComponent } from '@shared/widgets/components/map-widget/map-widget.component';
import { MapModule } from '@shared/map/map.module';
import { ButtonDsComponent } from '@design-system/components/molecules/button/button.component';
import { FixedSurfaceModule } from '@ux/components/fixed-surface/fixed-surface.module';
import { NotificationDsComponent } from '@design-system/components/molecules/notification/notification.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { ChipActionDsComponent } from '@design-system/components/molecules/chip-action/chip-action.component';
import { CheckboxDsComponent } from '@design-system/components/organisms/checkbox/checkbox.component';
import { ButtonLinkDsComponent } from '@design-system/components/molecules/button-link/button-link.component';

export const WIDGETS_REDUCER_TOKEN = new InjectionToken<
  ActionReducerMap<WidgetsState>
>('Feature Widgets Reducers');

@NgModule({
  declarations: [
    FlightsWidgetComponent,
    GeneralWidgetComponent,
    WidgetsContainerComponent,
    RefundableWidgetComponent,
    AlertsWidgetComponent,
    AlertNotificationComponent,
    TopZonesWidgetComponent,
    AlertFormComponent,
    DeleteAlertFormComponent,
    MapWidgetComponent
  ],
  imports: [
    CommonModule,
    StoreModule.forFeature(widgetsFeatureName, WIDGETS_REDUCER_TOKEN),
    EffectsModule.forFeature([AlertsEffects]),
    CarouselModule,
    ImageModule,
    TranslocoModule,
    GlobalPipesModule,
    SwipeUpModule,
    ModalModule,
    FormCurrencyInputModule,
    ReactiveFormsModule,
    MapModule,
    ButtonDsComponent,
    FixedSurfaceModule,
    NotificationDsComponent,
    IconDsComponent,
    TypographyDsComponent,
    ChipActionDsComponent,
    CheckboxDsComponent,
    ButtonLinkDsComponent,
  ],
  exports: [
    FlightsWidgetComponent,
    GeneralWidgetComponent,
    WidgetsContainerComponent,
    RefundableWidgetComponent,
    AlertsWidgetComponent,
    AlertNotificationComponent,
    AlertFormComponent,
    DeleteAlertFormComponent,
    MapWidgetComponent,
    TopZonesWidgetComponent
  ],
  providers: [
    HotelResultsFacade,
    FlightResultsFacade,
    WidgetsFacade,
    AlertsService,
    {
      provide: WIDGETS_REDUCER_TOKEN,
      useValue: widgetsRootReducer
    },
    BookingFacade,
    CountryNamePipe,
    FormValidationService,
    DatePipe,
  ]
})
export class WidgetsModule { }
